import React from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useForm, SubmitHandler } from "react-hook-form";
import { useAuth } from "../contexts/AuthContext";

import toast from "react-hot-toast";
import { BodySmallRegular, Heading4 } from "../components/ui/Typography";
import "../styles/background.css";
import CustomInput from "../components/ui/CustomInput";
import { ReactComponent as Logo } from "../assets/logo.svg";
import { ReactComponent as EmailIcon } from "../assets/ic_mail.svg";
import { ReactComponent as PasswordIcon } from "../assets/ic_lock.svg";

import PrimaryButton from "../components/ui/PrimaryButton";
import { LoginRequest } from "../api/services/auth/type";

interface LocationState {
  from?: {
    pathname: string;
  };
}

export const Login: React.FC = () => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    clearErrors,
  } = useForm<LoginRequest>({
    mode: "onBlur",
    defaultValues: {
      identifier: "",
      password: "",
    },
  });

  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (isAuthenticated) {
    const from = (location.state as LocationState)?.from?.pathname || "/";
    return <Navigate to={from} replace />;
  }

  const onSubmit: SubmitHandler<LoginRequest> = async (data) => {
    clearErrors("root");

    try {
      await login(data);
      const from = (location.state as LocationState)?.from?.pathname || "/";
      navigate(from, { replace: true });
    } catch (err) {
      toast.error((err as Error).message || "Login failed");
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-md w-full space-y-8">
        <div className="flex justify-center">
          <Logo className="w-42 h-42 mb-4" />
        </div>
        <div>
          <Heading4 className="mt-6 text-center text-gray-900 capitalize">
            Welcome Back
          </Heading4>
        </div>
        <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <BodySmallRegular className="text-gray-300 m-0 mb-1">
              Email / Username
            </BodySmallRegular>
            <CustomInput<LoginRequest>
              icon={<EmailIcon />}
              placeholder="Enter your email address"
              type="text"
              name="identifier"
              register={register}
              validation={{
                required: "Email is required",
              }}
              error={errors.identifier}
              disabled={isSubmitting}
            />
          </div>
          <div>
            <BodySmallRegular className="text-gray-300 m-0 mb-1">
              Password
            </BodySmallRegular>
            <CustomInput<LoginRequest>
              icon={<PasswordIcon />}
              placeholder="Enter your password"
              type="password"
              name="password"
              register={register}
              validation={{
                required: "Password is required",
                minLength: {
                  value: 6,
                  message: "Password must be at least 6 characters",
                },
              }}
              error={errors.password}
              disabled={isSubmitting}
            />
          </div>

          <PrimaryButton
            type="submit"
            isLoading={isSubmitting}
            loadingText="Logging in..."
            disabled={isSubmitting}
            className="w-full"
          >
            Login
          </PrimaryButton>
        </form>{" "}
      </div>
    </div>
  );
};
